import { Info } from "lucide-react";
import { useState, useRef, useEffect } from "react";

/**
 * AdminFieldInfo - reusable tooltip popover that documents an admin field.
 *
 * Iter 9.29: every admin field across Category Master, Copy Editor, Applications,
 * Product hub, /me Template Editor, etc. should sprout one of these.
 *
 * Props:
 *   description (string)  - What this field controls (1-2 sentences).
 *   syncs_to (string[])   - Where the value appears or is read by:
 *                           e.g. ["/me Hosting CTA card", "Sales pages hero", "Copy Editor key host.cta"]
 *   source (string)       - Backing collection / endpoint, e.g. "company_settings.hosting_cta_title"
 *                           or "users.locations[].city".
 *   side ("right"|"left"|"top"|"bottom") - tooltip direction. Default "right".
 *   testid (string)       - optional - appended to the trigger as data-testid="info-{testid}".
 */
export default function AdminFieldInfo({ description, syncs_to = [], source, side = "right", testid }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const sideClass = {
    right: "left-full ml-2 top-0",
    left: "right-full mr-2 top-0",
    top: "bottom-full mb-2 left-0",
    bottom: "top-full mt-2 left-0",
  }[side] || "left-full ml-2 top-0";

  return (
    <span ref={wrapRef} className="relative inline-flex">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        className="text-gray-400 hover:text-indigo-600 transition-colors"
        title="Field info"
        data-testid={testid ? `admin-info-${testid}` : undefined}
        aria-label="Field info"
      >
        <Info className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div
          className={`absolute z-50 ${sideClass} w-72 max-w-[80vw] rounded-lg bg-gray-900 text-white shadow-xl p-3 text-[11px] leading-relaxed`}
          role="tooltip"
          data-testid={testid ? `admin-info-tip-${testid}` : "admin-info-tip"}
        >
          <p className="font-semibold text-[12px] mb-1.5">{description}</p>
          {syncs_to.length > 0 && (
            <>
              <p className="uppercase font-bold text-[9px] tracking-wider text-gray-400 mt-2 mb-1">Syncs to</p>
              <ul className="list-disc pl-4 space-y-0.5 text-gray-200">
                {syncs_to.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </>
          )}
          {source && (
            <>
              <p className="uppercase font-bold text-[9px] tracking-wider text-gray-400 mt-2 mb-1">Source</p>
              <code className="text-[10px] text-gray-300 break-all">{source}</code>
            </>
          )}
        </div>
      )}
    </span>
  );
}
